import React from 'react';
import { PageType } from '../App';

interface AboutPageProps {
  setCurrentPage: (page: PageType) => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ setCurrentPage }) => {
  return (
    <>
      <section className="uc-hero">
        <div className="uc-hero-inner">
          <span className="uc-hero-label">About Us</span>
          <h1>We've seen what bad<br/><em>expansion decisions cost.</em></h1>
          <p>Market Entry was built by three ex-McKinsey researchers who spent years watching the same expensive mistake play out across boardrooms and growth strategies. They decided to do something about it.</p>
        </div>
      </section>

      <div className="uc-section">
        <div className="uc-section-inner">
          <div style={{maxWidth:'760px',margin:'0 auto'}}>
            <div className="section-label"><span>The Origin</span></div>
            <h2 className="section-title">A $25,000 PDF<br/><em>that answered the wrong question.</em></h2>
            <p className="section-sub">This is where the idea started.</p>

            <div className="about-story-list">
              <div className="about-story-item">
                <div className="about-story-num">01</div>
                <div>
                  <h3 className="about-story-h3">The problem we kept seeing</h3>
                  <p className="about-story-p">Across hundreds of engagements at McKinsey, we worked with companies planning to expand into new markets. Almost every one of them had commissioned a market research report — thick documents that described the market, listed competitors, and outlined regulatory considerations. Most cost between $15,000 and $30,000. And almost none of them answered the question that actually mattered: <em style={{color:'var(--forest)',fontStyle:'italic'}}>will this expansion be profitable, and when?</em></p>
                </div>
              </div>
              <div className="about-story-item">
                <div className="about-story-num">02</div>
                <div>
                  <h3 className="about-story-h3">What companies actually needed</h3>
                  <p className="about-story-p">What leadership teams really needed wasn't a description of a market. They needed to see how their specific expansion would most likely play out — month by month, across the first 12 months. True hiring costs. Local tax exposure. When the cash runs out. When the revenue starts covering the costs. And critically: whether the market they had chosen was actually the best one, or whether a neighbouring country offered a faster path to profitability at lower risk.</p>
                </div>
              </div>
              <div className="about-story-item">
                <div className="about-story-num">03</div>
                <div>
                  <h3 className="about-story-h3">So we built it — and made it free</h3>
                  <p className="about-story-p">Three of us left consulting to build the tool we had always wished existed. A simulation engine that takes your specific expansion plan and models what it will actually look like — not as a static report, but as a living projection across conservative, base and optimistic scenarios. We built it to be delivered in 72 hours. And we made it completely free, because the companies that need this most are often the ones that can't afford a $25,000 PDF.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="uc-section-dark">
        <div className="uc-section-inner" style={{textAlign:'center'}}>
          <div className="section-label" style={{borderColor:'rgba(255,255,255,0.12)',justifyContent:'center'}}><span style={{color:'rgba(255,255,255,0.5)'}}>What we built</span></div>
          <h2 className="section-title" style={{color:'var(--white)'}}>Not a report.<br/><em style={{color:'var(--sage-light)'}}>A simulation.</em></h2>
          <p className="section-sub" style={{color:'rgba(255,255,255,0.45)',maxWidth:'600px',marginLeft:'auto',marginRight:'auto'}}>There's a fundamental difference between describing a market and simulating what happens when your company enters it. We do the latter.</p>
          <div className="about-features-grid">
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'16px',padding:'28px'}}>
              <div style={{fontFamily:'DM Mono,monospace',fontSize:'11px',letterSpacing:'0.12em',textTransform:'uppercase' as const,color:'rgba(124,154,110,0.7)',marginBottom:'16px'}}>Your expansion</div>
              <h3 style={{fontFamily:'Libre Baskerville,serif',fontSize:'18px',color:'var(--white)',marginBottom:'12px',lineHeight:'1.3'}}>12-month revenue and cost simulation</h3>
              <p style={{fontSize:'13.5px',color:'rgba(255,255,255,0.4)',lineHeight:'1.65'}}>See exactly when you break even, when cash peaks, and what your three most likely paths to profitability look like.</p>
            </div>
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'16px',padding:'28px'}}>
              <div style={{fontFamily:'DM Mono,monospace',fontSize:'11px',letterSpacing:'0.12em',textTransform:'uppercase' as const,color:'rgba(124,154,110,0.7)',marginBottom:'16px'}}>Your market</div>
              <h3 style={{fontFamily:'Libre Baskerville,serif',fontSize:'18px',color:'var(--white)',marginBottom:'12px',lineHeight:'1.3'}}>True local costs, not estimates</h3>
              <p style={{fontSize:'13.5px',color:'rgba(255,255,255,0.4)',lineHeight:'1.65'}}>Real salary benchmarks, employer taxes, setup fees and compliance costs — so the number you commit to is the number you actually need.</p>
            </div>
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'16px',padding:'28px'}}>
              <div style={{fontFamily:'DM Mono,monospace',fontSize:'11px',letterSpacing:'0.12em',textTransform:'uppercase' as const,color:'rgba(124,154,110,0.7)',marginBottom:'16px'}}>Your options</div>
              <h3 style={{fontFamily:'Libre Baskerville,serif',fontSize:'18px',color:'var(--white)',marginBottom:'12px',lineHeight:'1.3'}}>Market comparison across similar markets</h3>
              <p style={{fontSize:'13.5px',color:'rgba(255,255,255,0.4)',lineHeight:'1.65'}}>Your target market ranked against alternatives — so you know if there's a faster, cheaper, lower-risk path you haven't considered.</p>
            </div>
          </div>
        </div>
      </div>

      <section className="bottom-cta">
        <div className="bottom-cta-inner">
          <h2>Ready to see your expansion<br/><em>before you commit?</em></h2>
          <p className="sub">Submit your target market and receive a complete 12-month simulation within 72 hours.</p>
          <a href="#" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }} className="uc-hero-cta" style={{display:'inline-block',marginTop:'8px'}}>Start Your Free Simulation</a>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
